import { useState } from "react";
import { ShoppingBag, X, Trash2, Loader2, Mail, Check, Plus, Minus, ExternalLink, CreditCard, ArrowLeft } from "lucide-react";
import { useQuoteCart, type QuoteCartItem } from "@/lib/quote-cart";
import { supabase } from "@/integrations/supabase/client";
import {
  calculateTotals,
  formatUsd,
  getPriceCents,
  isPurchasable,
  FREE_SHIPPING_THRESHOLD_CENTS,
  FLAT_SHIPPING_CENTS,
  PICKUP_LOCATION,
  PICKUP_HOURS,
  TAX_RATE,
} from "@/lib/shop-pricing";
import { isCheckoutConfigured } from "@/lib/stripe";
import {
  validateTexasZip,
  normalizeZip,
  isTexasZip,
  isValidUsZip,
  extractZip,
} from "@/lib/texas-zip";

import {
  ShopEmbeddedCheckout,
  type ShopCheckoutCustomer,
} from "@/components/ShopEmbeddedCheckout";


// ---------------------------------------------------------------------------
// QuoteCartButton — floating button shown site-wide once items exist.
// ---------------------------------------------------------------------------
export function QuoteCartButton() {
  const { count, open, isOpen } = useQuoteCart();
  if (count === 0 || isOpen) return null;
  return (
    <button
      type="button"
      onClick={open}
      aria-label={`Open your order — ${count} item${count === 1 ? "" : "s"}`}
      className="fixed bottom-5 right-5 z-[90] inline-flex items-center gap-3 border-4 border-navy-deep bg-red px-5 py-3 font-display text-sm uppercase tracking-wider text-white shadow-bold transition hover:bg-navy-deep"
    >
      <ShoppingBag className="h-5 w-5" />
      <span>My Order</span>
      <span className="inline-flex h-7 min-w-7 items-center justify-center rounded-full bg-white px-2 font-display text-xs font-bold text-navy-deep">
        {count}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// QuoteCartDrawer — slide-in drawer with items + checkout form.
// ---------------------------------------------------------------------------
export function QuoteCartDrawer() {
  const { items, isOpen, close, removeItem, updateQuantity, updateNotes, clear, count } = useQuoteCart();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payIntent, setPayIntent] = useState(false);
  const [fulfillment, setFulfillment] = useState<"ship" | "pickup">("ship");
  const [outOfState, setOutOfState] = useState<string | null>(null);
  /** Selected state — controlled so we can relax Texas-only field rules. */
  const [stateSel, setStateSel] = useState("");
  /** Shipping ZIP + its validation message (Texas-only gate). */
  const [zip, setZip] = useState("");
  const [zipError, setZipError] = useState<string | null>(null);
  const [checkout, setCheckout] = useState<{
    items: { productId: string; name: string; quantity: number; notes?: string }[];
    customer: ShopCheckoutCustomer;
  } | null>(null);

  // Pricing — only catalog items with a published price can be paid by card.
  const pricedLines = items.filter((i) => isPurchasable(i.productId));
  const quoteOnlyLines = items.filter((i) => !isPurchasable(i.productId));
  const allPriced = items.length > 0 && quoteOnlyLines.length === 0;
  const totals = calculateTotals(
    pricedLines.map((i) => ({ productId: i.productId, quantity: i.quantity })),
    { pickup: fulfillment === "pickup" },
  );
  const canPayByCard = allPriced && isCheckoutConfigured();

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (items.length === 0) {
      setError("Add at least one item before submitting your order.");
      return;
    }
    setSubmitting(true);

    setError(null);
    const fd = new FormData(e.currentTarget);
    const customer_name = String(fd.get("name") || "").trim();
    const customer_email = String(fd.get("email") || "").trim();
    const customer_phone = String(fd.get("phone") || "").trim() || null;
    const street = String(fd.get("address") || "").trim();
    const city = String(fd.get("city") || "").trim();
    const stateVal = String(fd.get("state") || "").trim();
    const shipping_address =
      [street, [city, stateVal === "TX" ? "TX" : stateVal, normalizeZip(String(fd.get("zip") || "")) || null].filter(Boolean).join(", ")]
        .filter(Boolean)
        .join(" — ") || null;
    const orderNotes = String(fd.get("orderNotes") || "").trim();
    const marketingOptIn = fd.get("marketingOptIn") != null;

    if (!customer_name || !customer_email) {
      setError("Please add your name and email so we can send your quote.");
      setSubmitting(false);
      return;
    }

    // Outside Texas — don't dead-end the shopper. Route them to a short
    // contact form so we can still help (referrals, freight quotes, custom work).
    if (stateVal !== "TX") {
      setOutOfState(customer_email || null);
      setError(null);
      setSubmitting(false);
      return;
    }

    // ZIP gate — blocks non-Texas addresses automatically. Only applies to
    // shipped orders; pickup happens at our Seguin shop.
    let zipVal = "";
    if (fulfillment === "ship") {
      zipVal = normalizeZip(String(fd.get("zip") || ""));
      const zipMsg = validateTexasZip(zipVal);
      if (zipMsg) {
        setZipError(zipMsg);
        // A confirmed out-of-state ZIP isn't a typo — route them to the
        // contact form instead of dead-ending on an error.
        if (isValidUsZip(zipVal) && !isTexasZip(zipVal)) {
          setOutOfState(customer_email || null);
          setZipError(null);
        } else {
          setError(zipMsg);
        }
        setSubmitting(false);
        return;
      }
      // Catch a non-Texas ZIP typed into the street address line too.
      const streetZip = extractZip(street);
      if (streetZip && streetZip !== zipVal && !isTexasZip(streetZip)) {
        const msg = `The ZIP in your street address (${streetZip}) isn't in Texas. We ship to Texas (TX) addresses only.`;
        setZipError(msg);
        setError(msg);
        setSubmitting(false);
        return;
      }
      setZipError(null);
    }

    // Mailing list opt-in — fire and forget so it can never block an order.
    if (marketingOptIn && customer_email) {
      try {
        const { subscribeToMailingList } = await import(
          "@/lib/mailing-list.functions"
        );
        void subscribeToMailingList({
          data: {
            email: customer_email,
            name: customer_name,
            source: "shop_cart",
          },
        });
      } catch (err) {
        console.error("mailing list opt-in failed", err);
      }
    }

    // Card checkout path — hand the order to secure hosted payment.
    if (payIntent) {
      setCheckout({
        items: items.map((i) => ({
          productId: i.productId,
          name: [i.name, i.size].filter(Boolean).join(" — "),
          quantity: i.quantity,
          notes: i.notes,
        })),
        customer: {
          customerName: customer_name,
          customerEmail: customer_email,
          customerPhone: customer_phone ?? undefined,
          street: street || undefined,
          city: fulfillment === "pickup" ? city || "Seguin" : city,
          state: "TX",
          zip: zipVal || undefined,
          fulfillment,
          orderNotes:
            [
              fulfillment === "pickup"
                ? `LOCAL PICKUP at ${PICKUP_LOCATION}`
                : null,
              orderNotes || null,
            ]
              .filter(Boolean)
              .join("\n") || undefined,
        },
      });
      setSubmitting(false);
      return;
    }


    // Build a human-readable items summary for the legacy notes field
    // (so existing email templates show the full list even before per-item
    // rendering is added).
    const itemsSummary = items
      .map((i, idx) => {
        const lines = [
          `${idx + 1}. ${i.name}${i.size ? ` — ${i.size}` : ""} × ${i.quantity}`,
        ];
        if (i.sourceUrl) lines.push(`   Ref: ${i.sourceUrl}`);
        if (i.notes) lines.push(`   Note: ${i.notes}`);
        return lines.join("\n");
      })
      .join("\n");

    const fulfillmentNote =
      fulfillment === "pickup"
        ? `Fulfillment: LOCAL PICKUP at ${PICKUP_LOCATION} (${PICKUP_HOURS})`
        : "Fulfillment: Ship to the Texas address above";

    const combinedNotes =
      [fulfillmentNote, `Multi-item order request (${count} item${count === 1 ? "" : "s"}):\n${itemsSummary}`, orderNotes && `\nCustomer notes:\n${orderNotes}`]
        .filter(Boolean)
        .join("\n");

    // Save to custom_quote_requests so we have a DB record.
    const { data: inserted, error: dbError } = await supabase
      .from("custom_quote_requests")
      .insert({
        customer_name,
        customer_email,
        customer_phone,
        shipping_address,
        product_id: "multi-item-order",
        product_name: `Order with ${items.length} item${items.length === 1 ? "" : "s"}`,
        quantity: count,
        notes: combinedNotes,
        ...(await import("@/lib/submission-source")).getSubmissionSource("custom_quote_cart"),
      } as any)
      .select("id")
      .single();
    if (dbError || !inserted?.id) {
      setError("Couldn't save your order. Please call us at +1 (830) 390-9588.");
      setSubmitting(false);
      return;
    }

    // Send the multi-item email
    try {
      await fetch("/api/public/order-notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind: "quote",
          submissionId: inserted.id,
          productName: `Order with ${items.length} item${items.length === 1 ? "" : "s"}`,
          productId: "multi-item-order",
          quantity: count,
          customerName: customer_name,
          customerEmail: customer_email,
          customerPhone: customer_phone,
          shippingAddress: shipping_address ?? undefined,
          notes: combinedNotes,
          items: items.map((i) => ({
            name: i.name,
            size: i.size,
            quantity: i.quantity,
            category: i.category,
            sourceUrl: i.sourceUrl,
            notes: i.notes,
          })),
        }),
      });
    } catch (err) {
      console.error("Failed to send order email", err);
    }

    setDone(true);
    setSubmitting(false);
  };

  const closeAndReset = () => {
    if (done) clear();
    setDone(false);
    setError(null);
    setOutOfState(null);
    close();
  };

  return (
    <div className="fixed inset-0 z-[100] flex" role="dialog" aria-modal="true" aria-label="Your order">
      <button
        type="button"
        aria-label="Close"
        className="flex-1 bg-navy-deep/70 backdrop-blur-sm"
        onClick={closeAndReset}
      />
      <aside className="relative flex h-full w-full max-w-md flex-col border-l-4 border-navy-deep bg-cream shadow-bold">
        {/* Header */}
        <div className="flex items-center justify-between border-b-4 border-navy-deep bg-navy-deep px-5 py-4 text-white">
          <div className="flex items-center gap-3">
            <ShoppingBag className="h-5 w-5" />
            <div>
              <div className="font-display text-[10px] uppercase tracking-[0.3em] text-white/60">
                ★ Your Order
              </div>
              <div className="font-display text-lg font-bold uppercase">
                {count} item{count === 1 ? "" : "s"}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={closeAndReset}
            aria-label="Close order"
            className="inline-flex h-9 w-9 items-center justify-center border border-white/30 text-white transition hover:bg-red"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Texas-only shipping banner — always visible */}
        {!done && (
          <div className="border-b-2 border-red/30 bg-red/10 px-5 py-2.5">
            <p className="font-display text-[11px] uppercase tracking-[0.18em] text-red">
              ★ Texas orders — ship or pick up
            </p>
            <p className="mt-1 text-[11px] leading-snug text-navy-deep/75">
              We ship inside the state of Texas (TX) only — for example San Antonio,
              Austin, New Braunfels, Seguin, Boerne, San Marcos, Houston, Dallas, Fort
              Worth, and El Paso. You can also pick your order up free at our Seguin,
              TX shop. Outside Texas (LA, OK, NM, AR and all others)? Choose your state
              below and we'll send you to a quick contact form instead.
            </p>
          </div>
        )}



        {done ? (
          <SuccessView onClose={closeAndReset} />
        ) : outOfState !== null ? (
          <OutOfStateView
            email={outOfState}
            onBack={() => setOutOfState(null)}
            onSent={() => {
              setOutOfState(null);
              setDone(true);
            }}
          />
        ) : checkout ? (
          <div className="flex flex-1 flex-col overflow-y-auto px-4 py-4">
            <button
              type="button"
              onClick={() => {
                setCheckout(null);
                setPayIntent(false);
              }}
              className="mb-3 inline-flex items-center gap-2 self-start font-display text-[11px] uppercase tracking-wider text-navy-deep/70 underline hover:text-red"
            >
              <ArrowLeft className="h-3 w-3" /> Back to my order
            </button>
            <div className="mb-3 border-2 border-navy-deep/15 bg-white px-4 py-3 font-display text-sm text-navy-deep">
              <div className="flex items-center justify-between">
                <span className="uppercase tracking-wider">Before tax</span>
                <span className="font-bold">{formatUsd(totals.preTaxTotalCents)}</span>
              </div>
              <p className="mt-1 text-[11px] font-normal normal-case tracking-normal text-navy-deep/60">
                {fulfillment === "pickup"
                  ? "Local pickup — no shipping charge. Texas sales tax is calculated below from your billing address. Pick up at "
                  : "Includes shipping. Texas sales tax is calculated below from your delivery address. Enter a Texas (TX) address only — e.g. San Antonio, Austin, New Braunfels, Seguin, or Houston. Non-Texas addresses will be declined at payment."}
                {fulfillment === "pickup" ? `${PICKUP_LOCATION} (${PICKUP_HOURS}).` : ""}
              </p>

            </div>

            <ShopEmbeddedCheckout items={checkout.items} customer={checkout.customer} />
          </div>
        ) : items.length === 0 ? (
          <EmptyView onClose={closeAndReset} />
        ) : (

          <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
            {/* Items list */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="font-display text-[11px] uppercase tracking-[0.25em] text-red">
                  ★ Items in your order
                </div>
                <button
                  type="button"
                  onClick={clear}
                  className="font-display text-[10px] uppercase tracking-wider text-navy-deep/60 underline hover:text-red"
                >
                  Clear all
                </button>
              </div>
              <ul className="space-y-3">
                {items.map((item) => (
                  <CartLineItem
                    key={item.id}
                    item={item}
                    unitPriceCents={getPriceCents(item.productId)}
                    onRemove={() => removeItem(item.id)}
                    onQty={(q) => updateQuantity(item.id, q)}
                    onNotes={(n) => updateNotes(item.id, n)}
                  />
                ))}
              </ul>

              {/* Order totals */}
              {totals.subtotalCents > 0 && (
                <div className="mt-5 border-2 border-navy-deep/15 bg-white px-4 py-3 text-sm text-navy-deep">
                  <div className="flex items-center justify-between">
                    <span>Subtotal</span>
                    <span className="font-display font-bold">{formatUsd(totals.subtotalCents)}</span>
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    <span>
                      {fulfillment === "pickup" ? "Local pickup (Seguin, TX)" : "Shipping (Texas)"}
                    </span>
                    <span className="font-display font-bold">
                      {totals.shippingCents === 0 ? "FREE" : formatUsd(totals.shippingCents)}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between">
                    <span>Texas sales tax</span>
                    <span className="font-display text-[11px] uppercase tracking-wider text-navy-deep/60">
                      Calculated at checkout
                    </span>
                  </div>
                  <div className="mt-2 flex items-center justify-between border-t-2 border-navy-deep/15 pt-2 font-display text-base font-bold uppercase">
                    <span>Total before tax</span>
                    <span>{formatUsd(totals.preTaxTotalCents)}</span>
                  </div>
                  <p className="mt-1 text-[11px] text-navy-deep/60">
                    Sales tax is added on the payment step using your Texas
                    delivery address (approx. {(TAX_RATE * 100).toFixed(2)}%).
                  </p>

                  {fulfillment === "ship" && !totals.freeShipping && (
                    <p className="mt-2 text-[11px] text-navy-deep/60">
                      Add {formatUsd(FREE_SHIPPING_THRESHOLD_CENTS - totals.subtotalCents)} more for
                      free shipping — otherwise a flat {formatUsd(FLAT_SHIPPING_CENTS)}.
                    </p>
                  )}
                  {quoteOnlyLines.length > 0 && (
                    <p className="mt-2 text-[11px] text-navy-deep/70">
                      {quoteOnlyLines.length} item{quoteOnlyLines.length === 1 ? "" : "s"} in your
                      order {quoteOnlyLines.length === 1 ? "is" : "are"} priced by the yard or by
                      option, so we'll quote {quoteOnlyLines.length === 1 ? "it" : "them"} by email
                      instead of charging a card now.
                    </p>
                  )}
                </div>
              )}


              {/* Fulfillment choice — ship in Texas or pick up in Seguin */}
              <div className="mt-6 border-t-2 border-navy-deep/20 pt-5">
                <div className="font-display text-[11px] uppercase tracking-[0.25em] text-red">
                  ★ How would you like to get it?
                </div>
                <div className="mt-3 grid gap-2">
                  {([
                    {
                      key: "ship" as const,
                      title: "Ship to my Texas address",
                      detail: `Flat ${formatUsd(FLAT_SHIPPING_CENTS)} — free over ${formatUsd(FREE_SHIPPING_THRESHOLD_CENTS)}. Texas (TX) addresses only.`,
                    },
                    {
                      key: "pickup" as const,
                      title: "Local pickup — free",
                      detail: `${PICKUP_LOCATION} · ${PICKUP_HOURS}. We'll email you when it's ready.`,
                    },
                  ]).map((opt) => (
                    <label
                      key={opt.key}
                      className={`flex cursor-pointer items-start gap-3 border-2 px-3 py-2.5 transition ${
                        fulfillment === opt.key
                          ? "border-red bg-white"
                          : "border-navy-deep/15 bg-white/60 hover:border-navy-deep/40"
                      }`}
                    >
                      <input
                        type="radio"
                        name="fulfillment"
                        value={opt.key}
                        checked={fulfillment === opt.key}
                        onChange={() => {
                          setFulfillment(opt.key);
                          if (opt.key === "pickup" && stateSel === "") setStateSel("TX");
                        }}
                        className="mt-1 h-4 w-4 accent-red"
                      />
                      <span className="block">
                        <span className="block font-display text-xs uppercase tracking-wider text-navy-deep">
                          {opt.title}
                        </span>
                        <span className="mt-0.5 block text-[11px] leading-snug text-navy-deep/65">
                          {opt.detail}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Customer details */}
              <div className="mt-6 border-t-2 border-navy-deep/20 pt-5">
                <div className="font-display text-[11px] uppercase tracking-[0.25em] text-red">
                  ★ Your details
                </div>
                <p className="mt-1 mb-3 text-xs text-navy-deep/70">
                  We'll email a written quote — usually the same business day. No payment required now.
                  <span className="mt-1 block font-semibold text-navy-deep">
                    {fulfillment === "pickup"
                      ? `Picking up at ${PICKUP_LOCATION} — no shipping address needed.`
                      : "Texas (TX) delivery addresses only — accepted cities include San Antonio, Austin, New Braunfels, Seguin, Boerne, San Marcos, Houston, Dallas, Fort Worth, Corpus Christi, and El Paso."}
                  </span>
                  <span className="mt-1 block text-navy-deep/60">
                    Shopping from Louisiana, Oklahoma, New Mexico, Arkansas or another
                    state? Pick your state below — we'll open a contact form so we can
                    still help.
                  </span>
                </p>
                <div className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <input required name="name" placeholder="Your Name *" className={inputCls}  aria-label="Your Name"/>
                    <input required name="email" type="email" placeholder="Email *" className={inputCls}  aria-label="Email"/>
                  </div>
                  <input name="phone" placeholder="Phone Number" className={inputCls}  aria-label="Phone Number"/>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <input
                      required={fulfillment === "ship" && stateSel === "TX"}
                      name="city"
                      placeholder={
                        fulfillment === "pickup"
                          ? "Your City (optional)"
                          : "Texas City * (e.g. San Antonio)"
                      }
                      className={inputCls}
                      aria-label="City"
                    />
                    <select
                      required
                      name="state"
                      value={stateSel}
                      onChange={(e) => setStateSel(e.target.value)}
                      className={inputCls}
                      aria-label="State"
                    >
                      <option value="" disabled>Your State *</option>
                      <option value="TX">Texas (TX) — ship or pick up</option>
                      <option value="OUTSIDE">I'm outside Texas — contact me instead</option>
                    </select>
                  </div>

                  {/* ZIP is our automatic Texas check — city names repeat
                      across states, ZIP ranges don't. */}
                  {fulfillment === "ship" && (
                    <div>
                      <input
                        required={stateSel === "TX"}
                        name="zip"
                        inputMode="numeric"
                        maxLength={10}
                        value={zip}
                        onChange={(e) => {
                          setZip(e.target.value);
                          setZipError(null);
                        }}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          setZipError(v ? validateTexasZip(v) : null);
                        }}
                        placeholder="Texas ZIP Code * (e.g. 78155)"
                        className={`${inputCls} ${zipError ? "border-red" : ""}`}
                        aria-label="Texas ZIP Code"
                        aria-invalid={zipError ? true : undefined}
                      />
                      {zipError ? (
                        <p className="mt-1 text-xs font-semibold text-red">{zipError}</p>
                      ) : (
                        <p className="mt-1 text-[11px] text-navy-deep/60">
                          We verify the ZIP automatically — Texas ZIPs run 75001–79999
                          (plus El Paso's 885xx).
                        </p>
                      )}
                    </div>
                  )}

                  <textarea
                    name="address"
                    rows={2}
                    placeholder={
                      fulfillment === "pickup"
                        ? "Anything we should know for pickup? (optional)"
                        : "Texas street address (e.g. 123 Main St, Seguin)"
                    }
                    className={inputCls}
                   aria-label="Street Address"/>

                  <textarea
                    name="orderNotes"
                    rows={3}
                    placeholder="Order notes — anything else we should know?"
                    className={inputCls}
                   aria-label="Order notes — anything else we should know?"/>

                  <label className="flex items-start gap-2 text-xs leading-relaxed text-navy-deep/80">
                    <input
                      type="checkbox"
                      name="marketingOptIn"
                      defaultChecked
                      className="mt-0.5 h-4 w-4 shrink-0 accent-red"
                    />
                    <span>
                      Email me Texas Made Shade news — fabric care tips, new
                      supplies and shop specials. No spam, unsubscribe anytime.
                    </span>
                  </label>
                </div>

              </div>
              {error && <p className="mt-3 text-sm text-red">{error}</p>}
            </div>

            {/* Footer / submit */}
            <div className="border-t-4 border-navy-deep bg-white px-5 py-4">
              <div className="mb-3 flex items-center justify-between font-display text-sm text-navy-deep">
                <span className="uppercase tracking-wider">
                  {canPayByCard ? "Before tax" : "Items"}
                </span>
                <span className="font-bold">
                  {canPayByCard ? formatUsd(totals.preTaxTotalCents) : count}
                </span>
              </div>

              {canPayByCard && (
                <button
                  type="submit"
                  disabled={submitting}
                  onClick={() => setPayIntent(true)}
                  className="inline-flex w-full items-center justify-center gap-2 bg-red px-6 py-3.5 font-display text-sm uppercase tracking-wider text-white shadow-bold transition hover:bg-navy-deep disabled:opacity-60"
                >
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Starting checkout…</>
                  ) : (
                    <><CreditCard className="h-4 w-4" /> Pay Now — {formatUsd(totals.preTaxTotalCents)} + tax</>
                  )}
                </button>
              )}


              <button
                type="submit"
                disabled={submitting}
                onClick={() => setPayIntent(false)}
                className={`inline-flex w-full items-center justify-center gap-2 px-6 py-3.5 font-display text-sm uppercase tracking-wider transition disabled:opacity-60 ${
                  canPayByCard
                    ? "mt-2 border-2 border-navy-deep bg-white text-navy-deep hover:bg-navy-deep hover:text-white"
                    : "bg-red text-white shadow-bold hover:bg-navy-deep"
                }`}
              >
                {submitting && !payIntent ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</>
                ) : (
                  <><Mail className="h-4 w-4" /> {canPayByCard ? "Or Request a Written Quote" : "Submit Order — Get Quote"}</>
                )}
              </button>

              <p className="mt-2 text-center text-[11px] text-navy-deep/60">
                {canPayByCard
                  ? fulfillment === "pickup"
                    ? "Secure card checkout. Free local pickup at our Seguin, TX shop — we'll email you when it's ready."
                    : "Secure card checkout. Ships to Texas (TX) addresses only — San Antonio, Austin, Houston, Dallas and every other Texas city. Free shipping over $75."
                  : "We'll email a written quote you can approve, then an invoice you can pay online. Texas (TX) delivery addresses only."}
              </p>

            </div>
          </form>
        )}
      </aside>
    </div>
  );
}

function CartLineItem({
  item,
  unitPriceCents,
  onRemove,
  onQty,
  onNotes,
}: {
  item: QuoteCartItem;
  unitPriceCents?: number;
  onRemove: () => void;
  onQty: (q: number) => void;
  onNotes: (n: string) => void;
}) {

  const [showNotes, setShowNotes] = useState(Boolean(item.notes));
  return (
    <li className="border-2 border-navy-deep/15 bg-white p-3">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          {item.category && (
            <div className="font-display text-[10px] uppercase tracking-widest text-red">
              {item.category}
            </div>
          )}
          <div className="font-display text-sm font-bold uppercase leading-tight text-navy-deep">
            {item.name}
          </div>
          {item.size && (
            <div className="mt-0.5 text-xs text-navy-deep/70">{item.size}</div>
          )}
          <div className="mt-1 font-display text-xs text-navy-deep">
            {typeof unitPriceCents === "number" ? (
              <>
                {formatUsd(unitPriceCents)} ea
                {item.quantity > 1 && (
                  <span className="text-navy-deep/60">
                    {" "}
                    · {formatUsd(unitPriceCents * item.quantity)}
                  </span>
                )}
              </>
            ) : (
              <span className="text-navy-deep/60">Priced by quote</span>
            )}
          </div>

          {item.sourceUrl && (
            <a
              href={item.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-[11px] text-navy-deep/60 underline hover:text-red"
            >
              View details <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${item.name}`}
          className="inline-flex h-7 w-7 items-center justify-center text-navy-deep/70 transition hover:text-red"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3">
        {/* Qty stepper */}
        <div className="inline-flex items-center border border-navy-deep/30 bg-cream">
          <button
            type="button"
            onClick={() => onQty(item.quantity - 1)}
            disabled={item.quantity <= 1}
            aria-label="Decrease quantity"
            className="inline-flex h-8 w-8 items-center justify-center text-navy-deep transition hover:bg-navy-deep hover:text-white disabled:opacity-30"
          >
            <Minus className="h-3 w-3" />
          </button>
          <input
            type="number"
            min={1}
            value={item.quantity}
            onChange={(e) => onQty(Number(e.target.value) || 1)}
            className="h-8 w-12 border-x border-navy-deep/30 bg-white text-center font-display text-sm text-navy-deep [appearance:textfield] focus:outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            aria-label="Quantity"
          />
          <button
            type="button"
            onClick={() => onQty(item.quantity + 1)}
            aria-label="Increase quantity"
            className="inline-flex h-8 w-8 items-center justify-center text-navy-deep transition hover:bg-navy-deep hover:text-white"
          >
            <Plus className="h-3 w-3" />
          </button>
        </div>
        <button
          type="button"
          onClick={() => setShowNotes((s) => !s)}
          className="font-display text-[10px] uppercase tracking-wider text-navy-deep/60 underline hover:text-red"
        >
          {showNotes ? "Hide note" : "Add note"}
        </button>
      </div>

      {showNotes && (
        <textarea
          value={item.notes ?? ""}
          onChange={(e) => onNotes(e.target.value)}
          rows={2}
          placeholder="Per-item note (optional)…"
          aria-label="Per-item note (optional)"
          className={`${inputCls} mt-2`}
        />
      )}
    </li>
  );
}

function EmptyView({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
      <ShoppingBag className="h-12 w-12 text-navy-deep/30" />
      <h3 className="mt-4 font-display text-xl font-bold uppercase text-navy-deep">
        Your order is empty
      </h3>
      <p className="mt-2 max-w-xs text-sm text-navy-deep/70">
        Add cleaners, supplies and custom items from the shop. Submit them all together and we'll email you a written quote.
      </p>
      <button
        onClick={onClose}
        className="mt-6 inline-flex items-center gap-2 bg-navy-deep px-6 py-3 font-display text-xs uppercase tracking-wider text-white hover:bg-red"
      >
        Continue shopping
      </button>
    </div>
  );
}

function SuccessView({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red text-white">
        <Check className="h-8 w-8" />
      </div>
      <h3 className="mt-4 font-display text-2xl font-bold uppercase text-navy-deep">
        Order received!
      </h3>
      <div className="mt-3 max-w-sm space-y-2 text-sm text-navy-deep/80">
        <p>Thanks — we've got your order. We'll review your items and email a written quote with shipping, usually the same business day.</p>
        <p>Once you approve, we'll send a QuickBooks invoice you can pay online.</p>
      </div>
      <button
        onClick={onClose}
        className="mt-6 inline-flex items-center gap-2 bg-red px-6 py-3 font-display text-xs uppercase tracking-wider text-white hover:bg-navy-deep"
      >
        Done — close
      </button>
    </div>
  );
}

const inputCls =
  "w-full border border-navy-deep/20 bg-white px-3 py-2.5 text-sm text-navy-deep placeholder:text-navy-deep/70 focus:border-navy-deep focus:outline-none";

// ---------------------------------------------------------------------------
// OutOfStateView — shoppers outside Texas aren't blocked silently. We explain
// why we can't ship and collect their details so we can still help.
// ---------------------------------------------------------------------------
function OutOfStateView({
  email,
  onBack,
  onSent,
}: {
  email: string | null;
  onBack: () => void;
  onSent: () => void;
}) {
  const { items, count } = useQuoteCart();
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSending(true);
    setErr(null);
    const fd = new FormData(e.currentTarget);
    const name = String(fd.get("name") || "").trim();
    const mail = String(fd.get("email") || "").trim();
    const phone = String(fd.get("phone") || "").trim() || null;
    const stateName = String(fd.get("stateName") || "").trim();
    const message = String(fd.get("message") || "").trim();

    if (!name || !mail || !stateName) {
      setErr("Please add your name, email and state so we can reply.");
      setSending(false);
      return;
    }

    const itemsSummary = items
      .map((i, idx) => `${idx + 1}. ${i.name}${i.size ? ` — ${i.size}` : ""} × ${i.quantity}`)
      .join("\n");
    const notes = [
      `OUT-OF-STATE INQUIRY — customer is in ${stateName} (we ship inside Texas only).`,
      items.length ? `Items they were interested in:\n${itemsSummary}` : null,
      message ? `Message:\n${message}` : null,
    ]
      .filter(Boolean)
      .join("\n\n");

    const { data: inserted, error: dbError } = await supabase
      .from("custom_quote_requests")
      .insert({
        customer_name: name,
        customer_email: mail,
        customer_phone: phone,
        shipping_address: stateName,
        product_id: "out-of-state-inquiry",
        product_name: `Out-of-state inquiry (${stateName})`,
        quantity: Math.max(1, count),
        notes,
        ...(await import("@/lib/submission-source")).getSubmissionSource(
          "out_of_state_inquiry",
        ),
      } as any)
      .select("id")
      .single();

    if (dbError || !inserted?.id) {
      setErr("Couldn't send that. Please call us at +1 (830) 390-9588.");
      setSending(false);
      return;
    }

    try {
      await fetch("/api/public/order-notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind: "quote",
          submissionId: inserted.id,
          productName: `Out-of-state inquiry (${stateName})`,
          productId: "out-of-state-inquiry",
          quantity: Math.max(1, count),
          customerName: name,
          customerEmail: mail,
          customerPhone: phone,
          shippingAddress: stateName,
          notes,
          items: items.map((i) => ({
            name: i.name,
            size: i.size,
            quantity: i.quantity,
            category: i.category,
            sourceUrl: i.sourceUrl,
            notes: i.notes,
          })),
        }),
      });
    } catch (e2) {
      console.error("Failed to send out-of-state inquiry email", e2);
    }

    setSending(false);
    onSent();
  };

  return (
    <form onSubmit={submit} className="flex flex-1 flex-col overflow-y-auto px-5 py-4">
      <button
        type="button"
        onClick={onBack}
        className="mb-3 inline-flex items-center gap-2 self-start font-display text-[11px] uppercase tracking-wider text-navy-deep/70 underline hover:text-red"
      >
        <ArrowLeft className="h-3 w-3" /> Back to my order
      </button>

      <div className="border-2 border-red bg-white px-4 py-3">
        <p className="font-display text-[11px] uppercase tracking-[0.2em] text-red">
          ★ We ship inside Texas only
        </p>
        <p className="mt-2 text-sm leading-snug text-navy-deep/80">
          Online orders ship to Texas (TX) addresses only — and you're welcome to pick
          up free at our Seguin, TX shop. Since you're outside Texas, send us this
          quick note instead: we often can help with freight, custom fabrication, or
          point you to a supplier near you.
        </p>
      </div>

      <div className="mt-4 space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <input required name="name" placeholder="Your Name *" className={inputCls} aria-label="Your Name" />
          <input
            required
            name="email"
            type="email"
            defaultValue={email ?? ""}
            placeholder="Email *"
            className={inputCls}
            aria-label="Email"
          />
        </div>
        <input name="phone" placeholder="Phone Number" className={inputCls} aria-label="Phone Number" />
        <input
          required
          name="stateName"
          placeholder="Your State * (e.g. Louisiana, Oklahoma, New Mexico)"
          className={inputCls}
          aria-label="Your State"
        />
        <textarea
          name="message"
          rows={4}
          placeholder="What do you need? We'll reply with options."
          className={inputCls}
          aria-label="What do you need?"
        />
      </div>

      {err && <p className="mt-3 text-sm text-red">{err}</p>}

      <button
        type="submit"
        disabled={sending}
        className="mt-4 inline-flex w-full items-center justify-center gap-2 bg-red px-6 py-3.5 font-display text-sm uppercase tracking-wider text-white shadow-bold transition hover:bg-navy-deep disabled:opacity-60"
      >
        {sending ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
        ) : (
          <><Mail className="h-4 w-4" /> Send My Request</>
        )}
      </button>
      <p className="mt-2 text-center text-[11px] text-navy-deep/60">
        Or call us at +1 (830) 390-9588 — {PICKUP_HOURS}.
      </p>
    </form>
  );
}
